import { useEffect, useRef } from 'react';

/**
 * AdBanner - Reusable Google AdSense ad component
 * 
 * Usage:
 *   <AdBanner slot="1234567890" format="auto" />
 *   <AdBanner slot="1234567890" format="rectangle" />
 *   <AdBanner slot="1234567890" format="horizontal" />
 * 
 * When AdSense is not configured, shows a styled placeholder.
 */

const AD_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || 'ca-pub-3787349276600739';

const AdBanner = ({
    slot,
    format = 'auto',
    responsive = true,
    className = '',
    style = {}
}) => {
    const adRef = useRef(null);
    const isAdSenseReady = AD_CLIENT && typeof window !== 'undefined' && window.adsbygoogle;

    useEffect(() => {
        if (isAdSenseReady && adRef.current) {
            try {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            } catch (e) {
                console.error('AdSense error:', e);
            }
        }
    }, [isAdSenseReady]);

    const formatClass = {
        auto: 'ad-container--horizontal',
        horizontal: 'ad-container--horizontal',
        rectangle: 'ad-container--rectangle',
        vertical: 'ad-container--rectangle',
    }[format] || 'ad-container--horizontal';

    // Show placeholder when AdSense is not configured
    if (!isAdSenseReady) {
        return (
            <div
                className={`ad-container ${formatClass} rounded-lg my-4 ${className}`}
                style={style}
                data-testid="ad-banner-placeholder"
            >
                <div className="text-center py-4 px-6">
                    <p className="text-zinc-600 text-xs uppercase tracking-widest mb-1">
                        Publicidad
                    </p>
                    <p className="text-zinc-700 text-[10px]">
                        Espacio para anuncio
                    </p>
                </div>
            </div>
        );
    }

    // Real AdSense ad
    return (
        <div className={`ad-container ${formatClass} rounded-lg my-4 ${className}`} style={style}>
            <ins
                ref={adRef}
                className="adsbygoogle"
                style={{ display: 'block', ...style }}
                data-ad-client={AD_CLIENT}
                data-ad-slot={slot}
                data-ad-format={format}
                data-full-width-responsive={responsive ? 'true' : 'false'}
            />
        </div>
    );
};

export default AdBanner;
